import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {BasicObject, NavigatorScreenProps} from '@/types';
import {goBack, goTo, http} from '@utils';
import globalStore from '@/services/global.state';
import {postUserInfo, setScratchAuth} from '@/services/global.service';
import {userLogin} from './login.service';
import {apiSendPhoneCode, defaultPhoneCode} from '@/config';
import Privacy from '@components/business/privacy/privacy';
import {useGlobalStore} from '@/services/global.store';
import {setUniqueId} from '@/utils/moengage';
import CheckBoxSvg from './components/checkbox-svg';
import {trackEvent} from '@/utils/track-event';
import {postReport, TReportType, TSourceType} from '@/services/global.service';

const closeIcon = require('@components/assets/icons/close.webp');
const clearIcon = require('@components/assets/icons/clear.webp');
const phoneIcon = require('@components/assets/icons/phone.webp');
const otpIcon = require('@components/assets/icons/code.webp');
const invitationCodeIcon = require('@assets/icons/login/invitation-code.webp');

const LoginIcon = ({src, className}: {src: string; className?: string}) => (
  <img src={src} alt="" className={className || 'h-4 w-4 shrink-0'} />
);

const NextUILoadingSpinner = () => (
  <div className="relative inline-flex flex-col items-center justify-center gap-2">
    <div className="relative flex h-5 w-5">
      <i className="absolute h-full w-full animate-spinner-ease-spin rounded-full border-2 border-solid border-b-current border-l-transparent border-r-transparent border-t-transparent" />
      <i className="absolute h-full w-full animate-spinner-linear-spin rounded-full border-2 border-dotted border-b-current border-l-transparent border-r-transparent border-t-transparent opacity-75" />
    </div>
  </div>
);

const NavTitle = ({title}: {title: string}) => (
  <div className="relative z-50 flex h-11 w-full flex-none items-center bg-white shadow-[0_1px_4px_0_rgba(0,0,0,0.25)]">
    <div className="flex flex-1 items-center justify-center font-bold text-t1">
      {title}
    </div>
    <button
      type="button"
      className="absolute right-0 top-0 flex h-full items-center border-0 bg-transparent px-3"
      onClick={goBack}>
      <LoginIcon src={closeIcon} className="h-6 w-6" />
    </button>
  </div>
);

const ClearButton = ({show, onClick}: {show: boolean; onClick: () => void}) =>
  show ? (
    <button
      type="button"
      className="mr-6 border-0 bg-transparent p-0"
      onClick={onClick}>
      <LoginIcon src={clearIcon} className="h-3 w-3" />
    </button>
  ) : null;

const OtpButton = ({
  phone,
  loading,
  hasOTP,
  otpTime,
  onSend,
}: {
  phone: string;
  loading: boolean;
  hasOTP: boolean;
  otpTime: number;
  onSend: () => void;
}) => {
  const {i18n} = useTranslation();
  if (hasOTP) {
    return (
      <div className="flex h-8 w-12 items-center justify-center rounded bg-[#F2F6FF] p-3">
        <span className="text-xs text-t3">{otpTime}s</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      disabled={phone.trim().length <= 0 || loading}
      className="box-border inline-flex h-8 min-w-[4rem] items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded border-0 bg-[linear-gradient(180deg,#f2f6ff_0%,#dfe3ed_100%)] px-3 text-t2 shadow-[0_1px_0_0_#adb3c8] disabled:opacity-30"
      onClick={onSend}>
      {loading ? <NextUILoadingSpinner /> : null}
      <span className="text-xs font-bold">{i18n.t('login.label.get-otp')}</span>
    </button>
  );
};

const SingUp = (props: NavigatorScreenProps) => {
  const {i18n} = useTranslation();
  const params = (props.route.params as BasicObject) || {};
  const sucessPage = (params.sucessPage as string) || null;
  const sucessPageParams = (params.sucessPageParams as BasicObject) || null;
  const [userPhone, setUserPhone] = useState('');
  const [OTPCode, setOTPCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpTime, setOtpTime] = useState(59);
  const [invitaCode, setInvitaCode] = useState('');
  const otpTimeRef = useRef(59);
  const [hasOTP, setHasOTP] = useState(false);
  const [checkedOne, setCheckedOne] = useState(false);
  const [checkedTwo, setCheckedTwo] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const updateToken = useGlobalStore(state => state.updateToken);
  const setUserInfo = useGlobalStore(state => state.setUserInfo);

  useEffect(() => {
    setInvitaCode(localStorage.getItem('invitationCode') || '');
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (hasOTP && otpTimeRef.current > 1) {
        otpTimeRef.current -= 1;
        setOtpTime(otpTimeRef.current);
      } else {
        window.clearInterval(timer);
        setHasOTP(false);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [hasOTP]);

  const sendOtp = () => {
    if (otpLoading || !userPhone) {
      return;
    }
    setOtpLoading(true);
    http
      .post('app/sendCode', {
        userPhone: (apiSendPhoneCode ? defaultPhoneCode : '') + userPhone,
      })
      .then(() => {
        globalStore.globalTotal.next({
          type: 'success',
          message: 'Successfully',
        });
        otpTimeRef.current = 59;
        setOtpTime(otpTimeRef.current);
        setHasOTP(true);
      })
      .catch(() => {})
      .finally(() => setOtpLoading(false));
  };

  const handleSubmit = () => {
    if (btnLoading) {
      return;
    }
    setBtnLoading(true);
    trackEvent('registerClick');
    userLogin(
      (apiSendPhoneCode ? defaultPhoneCode : '') + userPhone,
      OTPCode,
      invitaCode,
      false,
    )
      .then(async res => {
        updateToken(res.token);
        globalStore.globalTotal.next({
          type: 'success',
          message: 'Successfully',
        });
        const data: BasicObject = {fromLogin: true};
        if (sucessPage) {
          data.sucessPage = sucessPage;
        }
        if (sucessPageParams) {
          data.sucessPageParams = sucessPageParams;
        }
        setScratchAuth();
        goTo('SetPassword', data);
        if (res.isNewUser) {
          postReport({
            reportType: TReportType.REGISTER,
            inviteCode: invitaCode,
            sourceType:
              localStorage.getItem('from') === 'im'
                ? TSourceType.IM
                : TSourceType.CHANNEL,
          });
        }
        const userInfo = await postUserInfo();
        setUserInfo(userInfo);
        const {userId, packageId} = userInfo;
        setUniqueId(`${userId}_${packageId}`);
      })
      .catch(() => {})
      .finally(() => setBtnLoading(false));
  };

  const disabled =
    !userPhone || !OTPCode || !checkedOne || !checkedTwo || btnLoading;

  return (
    <div className="h-full bg-white">
      <NavTitle title={i18n.t('login.label.sign')} />
      <div className="login-h5-form h-full w-full border-t border-[var(--bg-color-grey)] p-6">
        <div className="mt-4 flex items-center border-b border-[var(--bg-color-grey)] py-4">
          <LoginIcon src={phoneIcon} />
          <div className="ml-2 mr-7 border-r border-[#93979f] pl-1 pr-2 text-base font-medium text-t3">
            +{defaultPhoneCode}
          </div>
          <input
            className="ml-2 mr-4 flex-1 appearance-none border-0 bg-transparent p-0 text-base font-medium text-t1 shadow-none outline-0 placeholder:font-medium placeholder:text-t4 focus:border-0 focus:outline-0 focus:ring-0"
            type="text"
            placeholder={i18n.t('login.tip.phone')}
            value={userPhone}
            onChange={event => {
              const value = event.target.value;
              setUserPhone(value && /^[0-9]+$/.test(value) ? value : '');
            }}
            maxLength={10}
          />
          <ClearButton show={!!userPhone} onClick={() => setUserPhone('')} />
        </div>
        <div className="mt-4 flex items-center border-b border-[var(--bg-color-grey)] py-4">
          <LoginIcon src={otpIcon} />
          <input
            className="ml-2 mr-4 w-28 flex-1 appearance-none border-0 bg-transparent p-0 text-base font-medium text-t1 shadow-none outline-0 placeholder:font-medium placeholder:text-t4 focus:border-0 focus:outline-0 focus:ring-0"
            type="text"
            placeholder={i18n.t('login.tip.otp')}
            value={OTPCode}
            onChange={event => {
              const value = event.target.value;
              setOTPCode(value && /^[0-9]+$/.test(value) ? value : '');
            }}
            maxLength={6}
          />
          <ClearButton show={!!OTPCode} onClick={() => setOTPCode('')} />
          <OtpButton
            phone={userPhone}
            loading={otpLoading}
            hasOTP={hasOTP}
            otpTime={otpTime}
            onSend={sendOtp}
          />
        </div>
        <div className="mt-4 flex items-center border-b border-[var(--bg-color-grey)] py-4">
          <LoginIcon src={invitationCodeIcon} />
          <input
            className="ml-2 mr-4 w-28 flex-1 appearance-none border-0 bg-transparent p-0 text-base font-medium text-t1 shadow-none outline-0 placeholder:font-medium placeholder:text-t4 focus:border-0 focus:outline-0 focus:ring-0"
            type="text"
            placeholder={i18n.t('login.tip.referral-code')}
            value={invitaCode}
            onChange={event => {
              const value = event.target.value;
              setInvitaCode(value && /^[0-9a-zA-Z]+$/.test(value) ? value : '');
            }}
            maxLength={8}
          />
          <ClearButton show={!!invitaCode} onClick={() => setInvitaCode('')} />
        </div>
        {[
          {
            checked: checkedOne,
            onChange: setCheckedOne,
            label: i18n.t('login.tip.confrim18'),
          },
          {
            checked: checkedTwo,
            onChange: setCheckedTwo,
            label: i18n.t('login.tip.notify'),
          },
        ].map((item, index) => (
          <button
            key={index}
            type="button"
            className="mt-5 flex border-0 bg-transparent p-0 text-left"
            onClick={() => item.onChange(!item.checked)}>
            <div className={index === 1 ? 'w-5' : ''}>
              <CheckBoxSvg
                className={`flex-none ${
                  item.checked ? 'text-c1' : 'text-[var(--bg-color-grey)]'
                }`}
                visible={item.checked}
              />
            </div>
            <span className="ml-2 text-sm text-t3">{item.label}</span>
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          className={`mt-5 flex h-12 w-full items-center justify-center rounded-full border-0 bg-primary ${
            disabled ? 'opacity-50' : ''
          }`}
          onClick={handleSubmit}>
          <span className="text-base font-bold text-white">
            {btnLoading ? <NextUILoadingSpinner /> : i18n.t('login.label.next')}
          </span>
        </button>
        <div className="mt-5 flex items-center justify-center py-3">
          <span className="mr-3 text-base text-[#93979f]">
            {i18n.t('login.tip.has-account')}
          </span>
          <button
            type="button"
            className="border-0 bg-transparent p-0 text-sm font-bold text-[var(--basic-color-primary)] underline"
            onClick={() => goTo('Login')}>
            {i18n.t('login.tip.sign-in')}
          </button>
        </div>
        <Privacy />
      </div>
    </div>
  );
};

export default SingUp;
